import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ILoginUser, IPaginatedResponse, IRegisterOrganizationParticipants, IRegisterSingleParticipant } from '../types'
import {KCTEventDto, OrganizationAdminDto, ParticipantAdminDto, TFetchedNewsDto} from '../types/contracts'
import config from '../utils/config';

class ApiService {
  private axiosInstance: AxiosInstance;
  constructor(baseURL: string | undefined) {
    if (!baseURL) {
      console.error("Base URL is not defined");
      throw new Error("Base URL is not defined");
    }
    this.axiosInstance = axios.create({
      baseURL: config.baseURL,
      headers: {
        "Content-Type": "application/json",
      },
    });

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Add any response interceptors here
        return response;
      },
      (error) => {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          localStorage.removeItem("token");
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    );
  }

  public get<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.get<T>(url, config);
  }

  public post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(url, data, config);
  }

  public put<T>(
    url: string,
    data?: KCTEventDto,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.put<T>(url, data, config);
  }

  public delete<T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.delete<T>(url, config);
  }

  public check<T>(
    upsellId: string,
    participantMembershipCardNumber: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    const data = { upsellId, participantMembershipCardNumber };
    return this.axiosInstance.post<T>("/check", data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  }

  public getEvent<KCTEventDto>(
    id: string,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<KCTEventDto>> {
    return this.axiosInstance.get<KCTEventDto>(`/events/${id}`, config);
  }

  public getCurrentEvent<KCTEventDto>(
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<KCTEventDto>> {
    return this.axiosInstance.get<KCTEventDto>(`/events/current`, config);
  }

  public createEvent<KCTEventDto>(
    event: KCTEventDto,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<KCTEventDto>> {
    return this.axiosInstance.post<KCTEventDto>(`/events`, event, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  }

  public updateEvent<KCTEventDto>(
    eventId: string,
    event: KCTEventDto,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<KCTEventDto>> {
    return this.axiosInstance.put<KCTEventDto>(`/events/${eventId}`, event, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  }

  public createNews<TReq, TRes>(
      data: TReq,
      config?: AxiosRequestConfig
  ): Promise<AxiosResponse<TRes>> {
    return this.axiosInstance.post<TRes>(`/news`, data, {
      ...config,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        "Content-Type": "application/json",
        ...(config?.headers || {}),
      },
    });
  }

  public deleteNews<TNewsDto>(
      id: string,
      config?: AxiosRequestConfig
  ): Promise<AxiosResponse<TNewsDto>> {
    return this.axiosInstance.delete<TNewsDto>(`/news/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  }

  public updateNews<TNewsDto>(
      id: string,
      data: Partial<TFetchedNewsDto>,
      config?: AxiosRequestConfig
  ): Promise<AxiosResponse<TNewsDto>> {
    return this.axiosInstance.put<TNewsDto>(`/news/${id}`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  }

  public getNews<TNewsDto>(
      eventId: string,
      config?: AxiosRequestConfig
  ): Promise<AxiosResponse<TNewsDto[]>> {
    return this.axiosInstance.get<TNewsDto[]>(`/news/${eventId}`);
  }

  public getEvents<KCTEventDto>(
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<KCTEventDto[]>> {
    return this.axiosInstance.get<KCTEventDto[]>(`/events`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });
  }

  public registerSingleParticipant<T>(
    data: IRegisterSingleParticipant,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>("/registrations/single", data, config);
  }

  public registerOrganizationWithParticipants<T>(
    data: IRegisterOrganizationParticipants,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>(
      "/registrations/organization",
      data,
      config
    );
  }

  public loginUser<T>(
    data: ILoginUser,
    config?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    return this.axiosInstance.post<T>("/admin/login", data, config);
  }

  public fetchParticipants = async (
    id: string,
    pageNumber: number,
    pageSize: number,
    nameFilter: string
  ) => {
    return this.axiosInstance.get<IPaginatedResponse<ParticipantAdminDto>>(
      `/events/${id}/registrations`,
      {
        params: { pageNumber, pageSize, nameFilter },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
  };

  public regenerateCode = async (
    id: string
  ) => {
    return this.axiosInstance.patch<IPaginatedResponse<string>>(
      `/registrations/${id}/regenerate-code`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
  };

  public fetchOrganizations = async (
    id: string,
    pageNumber: number,
    pageSize: number,
    nameFilter: string,
  ) => {
    return this.axiosInstance.get<IPaginatedResponse<OrganizationAdminDto>>(
      `/events/${id}/organizations`,
      {
        params: { pageNumber, pageSize, nameFilter },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
  };
}

const apiService = new ApiService(config.baseURL);
export default apiService;